// src/pages/import/declaracoes-aduaneiras/DeclaracoesAduaneirasConsultPage.js
import { IconButton, Tooltip, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {
  MoreHoriz as MoreHorizIcon,
  Receipt as ReceiptIcon,
  Event as EventIcon,
  GetApp as GetAppIcon,
  ArrowDownward as ArrowDownwardIcon,
  Replay as ReplayIcon,
  Pageview as PageviewIcon,
} from '@material-ui/icons';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import QCXPopupState from '../../../../components/popup-state/QCXPopupState';
import SimpleConsultPageTemplate from '../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import { isLoadingStatus } from '../../../../utils/store/store-utils';
import { formatDate } from '../../../../utils/general/general-utils';
import { convertUTCStringToLocalDateTime } from '../../../../utils/general/dateTime-utils';

import {
  selectDeclaracoesAduaneirasList as selectDAList,
  selectDeclaracoesAduaneirasCount as selectDACount,
  selectDeclaracoesAduaneirasStatus as selectDAStatus,
} from '../../../../features/declaracoesAduaneiras/declaracoesAduaneirasSlice';

import {
  fetchAllDAImportacaoByFilterAsync,
  salvarDataVencimentoDaAsync,
} from '../../../../features/declaracoesAduaneiras/declaracoesAduaneirasThunks';
import QCXDatePicker from '../../../../shared-components/date-picker/QCXDatePicker';

export default function DeclaracoesAduaneirasConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const list = useSelector(selectDAList);
  const count = useSelector(selectDACount);
  const status = useSelector(selectDAStatus);
  const isLoading = useMemo(() => isLoadingStatus(status), [status]);

  const [lastPage, setLastPage] = useState(1);
  const [lastSize, setLastSize] = useState(10);
  const [lastFiltro, setLastFiltro] = useState('');

  const [dlgVencOpen, setDlgVencOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [vencDate, setVencDate] = useState(null);

  const refreshGrid = useCallback(() => {
    dispatch(
      fetchAllDAImportacaoByFilterAsync([
        { name: 'page', value: lastPage },
        { name: 'size', value: lastSize },
        { name: 'numero', value: lastFiltro },
      ])
    );
  }, [dispatch, lastPage, lastSize, lastFiltro]);

  const fetchFirstPage = useCallback(() => {
    setLastPage(1); setLastSize(10); setLastFiltro('');
    dispatch(
      fetchAllDAImportacaoByFilterAsync([
        { name: 'page', value: 1 },
        { name: 'size', value: 10 },
      ])
    );
  }, [dispatch]);

  useEffect(() => { fetchFirstPage(); }, [fetchFirstPage]);

  const handleConfigChange = useCallback(({ currentPage, pageSize, filterInputValue }) => {
    setLastPage(currentPage);
    setLastSize(pageSize);
    setLastFiltro(filterInputValue || '');
    dispatch(
      fetchAllDAImportacaoByFilterAsync([
        { name: 'page', value: currentPage },
        { name: 'size', value: pageSize },
        { name: 'numero', value: filterInputValue },
      ])
    );
  }, [dispatch]);

  const openVencDialog = (id) => { setSelectedId(id); setVencDate(null); setDlgVencOpen(true); };
  const handleSalvarVencimento = async () => {
    if (!selectedId || !vencDate) { setDlgVencOpen(false); return; }
    const payloadDate = new Date(vencDate).toISOString().slice(0, 10); // YYYY-MM-DD
    await dispatch(salvarDataVencimentoDaAsync({ id: selectedId, dataVencimento: payloadDate }));
    setDlgVencOpen(false);
    refreshGrid();
  };

  const columns = useMemo(() => [
    {
      field: 'processo',
      headerName: t('com.muralis.qcx.processo'),
      headerAlign: 'center',
      align: 'center',
      flex: 140,
      valueGetter: ({ row }) => row?.processo || '-',
    },
    {
      field: 'cliente',
      headerName: t('com.muralis.qcx.cliente.label'),
      flex: 180,
      valueGetter: ({ row }) => row?.cliente || '-',
    },
    {
      field: 'conhecimento',
      headerName: 'Conhecimento',
      flex: 160,
      valueGetter: ({ row }) => row?.conhecimento || '-',
    },
    {
      field: 'registro',
      headerName: 'Registro',
      headerAlign: 'center',
      align: 'center',
      flex: 120,
      valueGetter: ({ row }) => row?.registro || '-',
    },
    {
      field: 'dataRegistro',
      headerName: t('com.muralis.qcx.dataRegistro'),
      headerAlign: 'center',
      align: 'center',
      type: 'date',
      flex: 120,
      valueGetter: ({ row }) =>
        row?.dataRegistro
          ? formatDate(convertUTCStringToLocalDateTime(row.dataRegistro), 'DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY')
          : '-',
    },
    {
      field: 'dataVencimento',
      headerName: t('com.muralis.qcx.dataVencimento'),
      headerAlign: 'center',
      align: 'center',
      type: 'date',
      flex: 120,
      valueGetter: ({ row }) =>
        row?.dataVencimento
          ? formatDate(convertUTCStringToLocalDateTime(row.dataVencimento), 'DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY')
          : '-',
    },
    {
      field: 'id',
      headerName: 'Código',
      headerAlign: 'center',
      align: 'center',
      flex: 100,
      valueGetter: ({ row }) => row?.id || '-',
    },
    {
      field: 'actions',
      headerName: t('com.muralis.qcx.acoes.label'),
      headerAlign: 'center',
      align: 'center',
      flex: 220,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;
        const { id } = row;
        return (
          <QCXPopupState popupId={`popup-menu-da-${id}`}>
            {(popupState) => (
              <>
                <Tooltip title={t('com.muralis.qcx.maisOpcoes')} arrow>
                  <IconButton {...bindTrigger(popupState)}>
                    <MoreHorizIcon color="secondary" size={20} />
                  </IconButton>
                </Tooltip>
                <Menu {...bindMenu(popupState)}>

                  <MenuItem
                    onClick={(event) => {
                      popupState.close(event);
                      openVencDialog(id);
                    }}
                  >
                    <EventIcon fontSize="small" style={{ marginRight: 8 }} />
                    <Typography variant="inherit" style={{ fontSize: 12 }}>
                      {'INSERIR DATA VENCIMENTO'}
                    </Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={(event) => {
                      popupState.close(event);
                      event.stopPropagation();
                      history.push(`/importacao/declaracoes-aduaneiras/${id}/baixa`);
                    }}
                  >
                    <ArrowDownwardIcon fontSize="small" style={{ marginRight: 8 }} />
                    <Typography variant="inherit" style={{ fontSize: 12 }}>
                      {'BAIXA DE DA'}
                    </Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={(event) => {
                      popupState.close(event);
                      event.stopPropagation();
                      history.push(`/importacao/declaracoes-aduaneiras/${id}/estorno`);
                    }}
                  >
                    <ReplayIcon fontSize="small" style={{ marginRight: 8 }} />
                    <Typography variant="inherit" style={{ fontSize: 12 }}>
                      {'ESTORNO DE DA'}
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </QCXPopupState>
        );
      },
    },
  ], [t, history]);

  const breadcrumbs = [
    { link: { to: '/', name: t('com.muralis.qcx.inicio') } },
    { link: { to: t('com.muralis.qcx.url.moduloImportacao'), name: t('com.muralis.qcx.importacao.label') } },
    { text: { name: 'Declarações Adicionais' }, default: true },
  ];

  const templateProps = useMemo(() => ({
    page: {
      title: 'Declarações Adicionais',
      icon: <ReceiptIcon />,
      breadcrumbs,
    },
    table: {
      columns,
      rowsCount: count,
      onConfigChangeCallback: handleConfigChange,
    },
  }), [columns, count, handleConfigChange, t]);

  return (
    <>
      <SimpleConsultPageTemplate
        pageProps={templateProps.page}
        controlProps={templateProps.control}
        tableProps={templateProps.table}
        tableData={list}
        isLoading={isLoading}
        authInfo={authInfo}
        requiredRoles={['importacao-sda']}
      />

      <Dialog open={dlgVencOpen} onClose={() => setDlgVencOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{'Inserir data de vencimento'}</DialogTitle>
        <DialogContent>
          <QCXDatePicker
            label={'Data de vencimento'}
            value={vencDate}
            onChange={setVencDate}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgVencOpen(false)}>{'Cancelar'}</Button>
          <Button color="secondary" variant="contained" onClick={handleSalvarVencimento}>
            {'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
